import {
  keepPreviousData,
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from '@tanstack/react-query';

import type {
  AddContactInput,
  AddContactResult,
  AdminEstablishmentDto,
  AdminListPageDto,
  AdminListQueryDto,
  ContactTokenAccessDto,
  ContactTokenStatusDto,
  EnrichmentRefreshResult,
  EnrollInput,
  EnrollResult,
  InvitationInput,
  RevokeContactInput,
  SupplierClient,
  TransferMasterInput,
} from '@/api/supplier-client';
import { useSupplierClient } from '@/api/supplier-client-context';

// =============================================================================
// use-suppliers.ts — Hooks TanStack Query du référentiel fournisseur ADMIN.
// =============================================================================
// Lectures (liste A-03, fiche A-04) en useQuery ; commandes (enrôlement 2-actes,
// transitions, contacts, refresh) en useMutation (effets de bord serveur). Le
// client effectif vient du SupplierClientProvider (doublable, stub-first b.2).
//
// ZÉRO règle métier ici (GATE_0 D6 b.2) : l'autorité (RBAC, withTenant, émission
// d'events, validité des transitions) est l'API. Les hooks relaient l'intention
// et, après une mutation, invalident les queries impactées pour refléter l'état
// serveur. L'erreur API remonte telle quelle.

const SUPPLIERS_KEY = ['suppliers'] as const;

/** Clé de query de la liste A-03. */
export const suppliersListKey = [...SUPPLIERS_KEY, 'list'] as const;
/** Clé de query de la fiche A-04 d'un SIRET. */
export function supplierDetailKey(siret: string): readonly unknown[] {
  return [...SUPPLIERS_KEY, 'detail', siret];
}

/**
 * A-03 / T2-REGISTRE — GET registre admin cross-fournisseur PAGINÉ. `query` (recherche/filtres/tri/
 * pagination) est intégré à la `queryKey` : chaque combinaison est cachée à part. `keepPreviousData`
 * conserve la page précédente pendant le fetch de la suivante (pagination sans flash — ENF-01). La
 * `queryKey` reste préfixée par `suppliersListKey` : les invalidations post-mutation la matchent.
 */
export function useSuppliersList(
  query: AdminListQueryDto = {},
): UseQueryResult<AdminListPageDto, Error> {
  const client: SupplierClient = useSupplierClient();
  return useQuery<AdminListPageDto, Error>({
    queryKey: [...suppliersListKey, query],
    queryFn: () => client.listEstablishments(query),
    placeholderData: keepPreviousData,
  });
}

/** A-04 — GET fiche admin d'un établissement. */
export function useSupplierDetail(
  siret: string,
): UseQueryResult<AdminEstablishmentDto, Error> {
  const client: SupplierClient = useSupplierClient();
  return useQuery<AdminEstablishmentDto, Error>({
    queryKey: supplierDetailKey(siret),
    queryFn: () => client.getEstablishment(siret),
  });
}

/** Clé de query du statut du lien d'activation des interlocuteurs d'un SIRET (fiche A-04/§2.2). */
export function supplierContactsTokenStatusKey(siret: string): readonly unknown[] {
  return [...SUPPLIERS_KEY, 'token-status', siret];
}

/** §2.2 — GET statut du lien d'activation (Envoyé/Expiré/Activé) des interlocuteurs de la fiche. */
export function useSupplierContactsTokenStatus(
  siret: string,
): UseQueryResult<readonly ContactTokenStatusDto[], Error> {
  const client: SupplierClient = useSupplierClient();
  return useQuery<readonly ContactTokenStatusDto[], Error>({
    queryKey: supplierContactsTokenStatusKey(siret),
    queryFn: () => client.listContactsTokenStatus(siret),
  });
}

/** Clé de query de la vue enrichie « Accès & tokens » des interlocuteurs d'un SIRET (T6-TOKENS/§2.2). */
export function supplierContactsTokenAccessKey(siret: string): readonly unknown[] {
  return [...SUPPLIERS_KEY, 'token-access', siret];
}

/**
 * T6-TOKENS (§2.2) — GET vue enrichie « Accès & tokens » (statut + dates réelles + compteur/plafond de
 * renvoi + fenêtre TTL) des interlocuteurs de la fiche. READ-ONLY. Clé DISTINCTE de `token-status` : le
 * bloc « Accès & tokens » et la colonne du tableau sont deux consommateurs indépendants (TanStack dédupe
 * par clé — aucune interférence de cache).
 */
export function useSupplierContactsTokenAccess(
  siret: string,
): UseQueryResult<readonly ContactTokenAccessDto[], Error> {
  const client: SupplierClient = useSupplierClient();
  return useQuery<readonly ContactTokenAccessDto[], Error>({
    queryKey: supplierContactsTokenAccessKey(siret),
    queryFn: () => client.listContactsTokenAccess(siret),
  });
}

/** Argument générique des mutations ciblant un SIRET avec corps. */
interface SiretWith<TBody> {
  readonly siret: string;
  readonly body: TBody;
}

/**
 * §2.2 — POST renvoi du lien d'activation à un interlocuteur (par contactId). On invalide le statut
 * sur `onSettled` : le succès ET le 409 « déjà activé » doivent refléter l'état réel (refetch).
 */
export function useResendContactActivation(): UseMutationResult<
  void,
  Error,
  SiretWith<{ readonly contactId: string }>
> {
  const client: SupplierClient = useSupplierClient();
  const qc = useQueryClient();
  return useMutation<void, Error, SiretWith<{ readonly contactId: string }>>({
    mutationKey: [...SUPPLIERS_KEY, 'resend-activation'],
    mutationFn: ({ siret, body }) => client.resendContactActivation(siret, body.contactId),
    onSettled: (_data, _error, { siret }) => {
      void qc.invalidateQueries({ queryKey: supplierContactsTokenStatusKey(siret) });
      // T6-TOKENS : un renvoi fait ÉVOLUER le compteur/les dates de la vue « Accès & tokens » —
      // on rafraîchit la vue enrichie en plus du statut compact (compteur N/3 vivant, ENF-01).
      void qc.invalidateQueries({ queryKey: supplierContactsTokenAccessKey(siret) });
    },
  });
}

/** A-05 ACTE 1 — POST créer le fournisseur (Brouillon, MASTER Invité, AUCUN event). */
export function useEnrollSupplier(): UseMutationResult<
  EnrollResult,
  Error,
  SiretWith<EnrollInput>
> {
  const client: SupplierClient = useSupplierClient();
  const qc = useQueryClient();
  return useMutation<EnrollResult, Error, SiretWith<EnrollInput>>({
    mutationKey: [...SUPPLIERS_KEY, 'enroll'],
    mutationFn: ({ siret, body }) => client.enroll(siret, body),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: suppliersListKey });
    },
  });
}

/** A-05 ACTE 2 — POST envoyer l'invitation (Brouillon→Soumis + event). Acte SÉPARÉ. */
export function useSendInvitation(): UseMutationResult<
  void,
  Error,
  SiretWith<InvitationInput>
> {
  const client: SupplierClient = useSupplierClient();
  const qc = useQueryClient();
  return useMutation<void, Error, SiretWith<InvitationInput>>({
    mutationKey: [...SUPPLIERS_KEY, 'send-invitation'],
    mutationFn: ({ siret, body }) => client.sendInvitation(siret, body),
    onSuccess: (_data, { siret }) => {
      void qc.invalidateQueries({ queryKey: suppliersListKey });
      void qc.invalidateQueries({ queryKey: supplierDetailKey(siret) });
    },
  });
}

/**
 * Variable d'une transition PORTANT un motif (T3-SUSPENSION : suspend/refuse). `reason` OBLIGATOIRE
 * côté API — le hook relaie l'intention, la validation `min(1)` reste l'autorité serveur.
 */
export interface TransitionWithReason {
  readonly siret: string;
  readonly reason: string;
}

/**
 * Fabrique de hook pour une transition d'état. Paramétrée sur la VARIABLE de mutation `V` :
 *   - `string`               → transition SANS motif (reactivate/archive) ;
 *   - `TransitionWithReason` → transition AVEC motif (suspend/refuse, T3-SUSPENSION).
 * Le SIRET (pour l'invalidation des queries) est extrait uniformément de la variable, quelle que soit
 * sa forme. Invalidations inchangées (liste A-03 + fiche A-04 du SIRET).
 */
function useEstablishmentTransition<V extends string | TransitionWithReason>(
  mutationName: string,
  call: (client: SupplierClient, vars: V) => Promise<void>,
): UseMutationResult<void, Error, V> {
  const client: SupplierClient = useSupplierClient();
  const qc = useQueryClient();
  return useMutation<void, Error, V>({
    mutationKey: [...SUPPLIERS_KEY, mutationName],
    mutationFn: (vars: V) => call(client, vars),
    onSuccess: (_data, vars) => {
      const siret = typeof vars === 'string' ? vars : vars.siret;
      void qc.invalidateQueries({ queryKey: suppliersListKey });
      void qc.invalidateQueries({ queryKey: supplierDetailKey(siret) });
    },
  });
}

/** Refus manuel admin (Soumis→Refuse) — MOTIF OBLIGATOIRE. */
export function useRefuseEstablishment(): UseMutationResult<
  void,
  Error,
  TransitionWithReason
> {
  return useEstablishmentTransition('refuse', (c, v) => c.refuse(v.siret, v.reason));
}

/** Suspension manuelle admin (Actif→Suspendu) — DESTRUCTIF + MOTIF OBLIGATOIRE. */
export function useSuspendEstablishment(): UseMutationResult<
  void,
  Error,
  TransitionWithReason
> {
  return useEstablishmentTransition('suspend', (c, v) => c.suspend(v.siret, v.reason));
}

/** Réactivation manuelle admin (Suspendu→Actif) — SANS motif. */
export function useReactivateEstablishment(): UseMutationResult<
  void,
  Error,
  string
> {
  return useEstablishmentTransition('reactivate', (c, s) => c.reactivate(s));
}

/** Archivage manuel admin (no-DELETE) — DESTRUCTIF, SANS motif. */
export function useArchiveEstablishment(): UseMutationResult<void, Error, string> {
  return useEstablishmentTransition('archive', (c, s) => c.archive(s));
}

/** Révocation d'un contact par l'admin — DESTRUCTIF. */
export function useRevokeContact(): UseMutationResult<
  void,
  Error,
  SiretWith<RevokeContactInput>
> {
  const client: SupplierClient = useSupplierClient();
  const qc = useQueryClient();
  return useMutation<void, Error, SiretWith<RevokeContactInput>>({
    mutationKey: [...SUPPLIERS_KEY, 'revoke-contact'],
    mutationFn: ({ siret, body }) => client.revokeContact(siret, body),
    onSuccess: (_data, { siret }) => {
      void qc.invalidateQueries({ queryKey: supplierDetailKey(siret) });
    },
  });
}

/**
 * BUG2-ADD-CONTACT (DEC-18) — ajout d'un contact COLLABORATEUR PAR l'ADMIN. Après succès, on invalide
 * la fiche (nouveau contact « Invité » dans la table) ET le statut des liens d'activation (le contact
 * ajouté apparaît avec son statut « Envoyé »). L'erreur API (422 hors-domaine sans dérogation, etc.)
 * remonte telle quelle au composant.
 */
export function useAddContact(): UseMutationResult<
  AddContactResult,
  Error,
  SiretWith<AddContactInput>
> {
  const client: SupplierClient = useSupplierClient();
  const qc = useQueryClient();
  return useMutation<AddContactResult, Error, SiretWith<AddContactInput>>({
    mutationKey: [...SUPPLIERS_KEY, 'add-contact'],
    mutationFn: ({ siret, body }) => client.addContact(siret, body),
    onSuccess: (_data, { siret }) => {
      void qc.invalidateQueries({ queryKey: supplierDetailKey(siret) });
      void qc.invalidateQueries({ queryKey: supplierContactsTokenStatusKey(siret) });
      void qc.invalidateQueries({ queryKey: supplierContactsTokenAccessKey(siret) });
    },
  });
}

/** Transfert du rôle MASTER (admin) — DESTRUCTIF. */
export function useTransferMaster(): UseMutationResult<
  void,
  Error,
  SiretWith<TransferMasterInput>
> {
  const client: SupplierClient = useSupplierClient();
  const qc = useQueryClient();
  return useMutation<void, Error, SiretWith<TransferMasterInput>>({
    mutationKey: [...SUPPLIERS_KEY, 'transfer-master'],
    mutationFn: ({ siret, body }) => client.transferMaster(siret, body),
    onSuccess: (_data, { siret }) => {
      void qc.invalidateQueries({ queryKey: supplierDetailKey(siret) });
    },
  });
}

/** Refresh d'enrichissement on-demand (fail-open dégradé → saisie manuelle). */
export function useRefreshEnrichment(): UseMutationResult<
  EnrichmentRefreshResult,
  Error,
  string
> {
  const client: SupplierClient = useSupplierClient();
  const qc = useQueryClient();
  return useMutation<EnrichmentRefreshResult, Error, string>({
    mutationKey: [...SUPPLIERS_KEY, 'refresh-enrichment'],
    mutationFn: (siret: string) => client.refreshEnrichment(siret),
    onSuccess: (_data, siret) => {
      void qc.invalidateQueries({ queryKey: supplierDetailKey(siret) });
    },
  });
}
